// mcq.js
// Client-side schema + normalisation for the Question-bank module.
// Server-side authority lives in server/lib/mcqSampler.js (sampling +
// grading) and server/index.js validateMcqPayload (save-time validation).
//
// Seven template types currently supported:
//   - mcq           single-correct multiple choice
//   - true-false    two-button True / False
//   - multi-mcq     multi-select, partial credit
//   - cloze         fill-in-the-blank, partial credit per blank
//   - ordering      drag items into the correct sequence
//   - bucket-sort   drag items into the correct labelled bucket
//   - matching      drag right-column items to align with left-column
//
// Best/worst framing is a per-template hint that adds a "Pick the BEST /
// WORST answer" banner above the options (mcq + multi-mcq only).

export const QUESTION_DISCIPLINES = ['medical', 'pharmacy', 'nursing'];

// Difficulty bands surfaced in the editor + filter chips. Free-text on the
// API but constrained client-side so the picker stays predictable.
export const MCQ_DIFFICULTIES = ['easy', 'medium', 'hard'];
export const DIFFICULTY_LABELS = { easy: 'Easy', medium: 'Medium', hard: 'Hard' };
export const DIFFICULTY_BADGE_BG = { easy: 'success', medium: 'warning', hard: 'danger' };
export const DIFFICULTY_BADGE_TEXT = { easy: undefined, medium: 'dark', hard: undefined };

export const QBANK_TEMPLATE_TYPES = ['mcq', 'true-false', 'multi-mcq', 'cloze', 'ordering', 'bucket-sort', 'matching'];
export const TEMPLATE_TYPE_LABELS = {
  'mcq': 'Multiple choice',
  'true-false': 'True / false',
  'multi-mcq': 'Multi-select',
  'cloze': 'Fill in the blanks',
  'ordering': 'Put in order',
  'bucket-sort': 'Sort into buckets',
  'matching': 'Match pairs',
};
export const TEMPLATE_TYPE_ICONS = {
  'mcq': 'bi-ui-radios',
  'true-false': 'bi-toggle-on',
  'multi-mcq': 'bi-ui-checks',
  'cloze': 'bi-input-cursor-text',
  'ordering': 'bi-sort-down',
  'bucket-sort': 'bi-grid-3x3-gap',
  'matching': 'bi-arrow-left-right',
};

export const FRAMINGS = ['standard', 'best', 'worst'];
export const FRAMING_LABELS = {
  standard: 'Standard',
  best: 'Pick the BEST answer',
  worst: 'Pick the WORST answer',
};

const newId = () => `tpl-${Math.random().toString(36).slice(2, 10)}`;

export const emptyMcqTemplate = () => ({
  id: newId(),
  templateType: 'mcq',
  prompt: '',
  options: ['', ''],
  correctIndex: 0,
  shuffleOptions: true,
  framing: 'standard',
  disciplines: [],
  notes: '',
});

export const emptyTrueFalseTemplate = () => ({
  id: newId(),
  templateType: 'true-false',
  prompt: '',
  correctAnswer: true,
  disciplines: [],
  notes: '',
});

export const emptyMultiMcqTemplate = () => ({
  id: newId(),
  templateType: 'multi-mcq',
  prompt: '',
  options: ['', '', '', ''],
  correctIndices: [0],
  shuffleOptions: true,
  framing: 'standard',
  disciplines: [],
  notes: '',
});

export const emptyClozeTemplate = () => ({
  id: newId(),
  templateType: 'cloze',
  // Sample prompt with two markers so the editor demonstrates the syntax.
  prompt: 'Paracetamol 1 g is given every {{1}} hours, up to a daily max of {{2}} g.',
  blanks: [
    { answers: ['4', 'four'], caseSensitive: false },
    { answers: ['4'], caseSensitive: false },
  ],
  disciplines: [],
  notes: '',
});

const orderingItemId = () => `o-${Math.random().toString(36).slice(2, 8)}`;
const bucketId = () => `b-${Math.random().toString(36).slice(2, 8)}`;
const bucketItemId = () => `s-${Math.random().toString(36).slice(2, 8)}`;

export const emptyOrderingTemplate = () => {
  const items = [
    { id: orderingItemId(), text: 'Step 1' },
    { id: orderingItemId(), text: 'Step 2' },
    { id: orderingItemId(), text: 'Step 3' },
  ];
  return {
    id: newId(),
    templateType: 'ordering',
    prompt: 'Put these steps in the correct order.',
    items,
    shuffleItems: true,
    disciplines: [],
    notes: '',
  };
};

const matchingPairId = () => `m-${Math.random().toString(36).slice(2, 8)}`;

export const emptyMatchingTemplate = () => ({
  id: newId(),
  templateType: 'matching',
  prompt: 'Match each item on the left with its pair on the right.',
  pairs: [
    { id: matchingPairId(), left: '', right: '' },
    { id: matchingPairId(), left: '', right: '' },
    { id: matchingPairId(), left: '', right: '' },
  ],
  shuffleRight: true,
  disciplines: [],
  notes: '',
});

export const emptyBucketSortTemplate = () => {
  const b1 = bucketId();
  const b2 = bucketId();
  return {
    id: newId(),
    templateType: 'bucket-sort',
    prompt: 'Sort these items into the correct bucket.',
    buckets: [
      { id: b1, label: 'Bucket A' },
      { id: b2, label: 'Bucket B' },
    ],
    items: [
      { id: bucketItemId(), text: 'Item 1', bucketId: b1 },
      { id: bucketItemId(), text: 'Item 2', bucketId: b2 },
    ],
    shuffleItems: true,
    disciplines: [],
    notes: '',
  };
};

// Numeric fixed-answer template. Authoring is exposed in the Revision
// editor only; the main Question-bank editor doesn't surface it (numeric
// drills live in the Calc exercises module).
export const emptyFixedTemplate = () => ({
  id: newId(),
  templateType: 'fixed',
  prompt: '',
  expectedAnswer: '',
  unit: '',
  tolerance: { type: 'percent', value: 1 },
  disciplines: [],
  notes: '',
});

// Parameterised calculation template — formula + variables. The
// runtime sampler picks fresh numbers each play and grades to the
// declared tolerance. Authored in the Question library now (used to
// be exclusive to the calc-exercises module).
export const emptyCalcTemplate = () => ({
  id: newId(),
  templateType: 'calculation',
  prompt: '',
  formula: '',
  unit: '',
  variables: [],
  tolerance: { type: 'percent', value: 1 },
  disciplines: [],
  notes: '',
});

export const emptyTemplateOfType = (type) => {
  if (type === 'true-false') return emptyTrueFalseTemplate();
  if (type === 'multi-mcq') return emptyMultiMcqTemplate();
  if (type === 'cloze') return emptyClozeTemplate();
  if (type === 'ordering') return emptyOrderingTemplate();
  if (type === 'bucket-sort') return emptyBucketSortTemplate();
  if (type === 'matching') return emptyMatchingTemplate();
  if (type === 'fixed') return emptyFixedTemplate();
  if (type === 'calculation') return emptyCalcTemplate();
  return emptyMcqTemplate();
};

export const emptyMcqExercise = () => ({
  id: '',
  title: '',
  summary: '',
  status: 'draft',
  freeTierEligible: false,
  category: '',
  difficulty: '',
  // Flags this set as a curated revision exercise — exposed to students
  // through the Mixed-revision picker's "Revision sets" list. Defaults
  // to false so any plain question set the lecturer creates stays out
  // of the revision picker until they explicitly opt in.
  isCuratedRevisionSet: false,
  disciplineMode: false,
  mode: 'learn', // 'learn' (reflective) | 'challenge' (gamified)
  settings: {
    questionCount: 5,
    shuffle: true,
  },
  templates: [emptyMcqTemplate()],
});

function normaliseFraming(value) {
  return FRAMINGS.includes(value) ? value : 'standard';
}

function normaliseDisciplines(input) {
  return Array.isArray(input)
    ? input.filter((d) => QUESTION_DISCIPLINES.includes(d))
    : [];
}

function normaliseMcqTemplate(input) {
  const options = Array.isArray(input.options) && input.options.length
    ? input.options.map((o) => String(o ?? ''))
    : ['', ''];
  const correctIndex = Number.isFinite(Number(input.correctIndex))
    ? Math.max(0, Math.min(options.length - 1, Math.floor(Number(input.correctIndex))))
    : 0;
  return {
    id: input.id || newId(),
    templateType: 'mcq',
    prompt: String(input.prompt || ''),
    options,
    correctIndex,
    shuffleOptions: input.shuffleOptions !== false,
    framing: normaliseFraming(input.framing),
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

function normaliseTrueFalseTemplate(input) {
  return {
    id: input.id || newId(),
    templateType: 'true-false',
    prompt: String(input.prompt || ''),
    correctAnswer: input.correctAnswer === true || String(input.correctAnswer).toLowerCase() === 'true',
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

function normaliseMultiMcqTemplate(input) {
  const options = Array.isArray(input.options) && input.options.length >= 2
    ? input.options.map((o) => String(o ?? ''))
    : ['', '', '', ''];
  const raw = Array.isArray(input.correctIndices) ? input.correctIndices : [];
  const correctIndices = Array.from(
    new Set(
      raw
        .map((n) => Math.floor(Number(n)))
        .filter((n) => Number.isFinite(n) && n >= 0 && n < options.length)
    )
  ).sort((a, b) => a - b);
  return {
    id: input.id || newId(),
    templateType: 'multi-mcq',
    prompt: String(input.prompt || ''),
    options,
    correctIndices: correctIndices.length ? correctIndices : [0],
    shuffleOptions: input.shuffleOptions !== false,
    framing: normaliseFraming(input.framing),
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

function normaliseClozeTemplate(input) {
  const rawBlanks = Array.isArray(input.blanks) ? input.blanks : [];
  const blanks = rawBlanks.map((b) => ({
    answers: Array.isArray(b?.answers) ? b.answers.map((a) => String(a ?? '')) : [],
    caseSensitive: b?.caseSensitive === true,
  }));
  return {
    id: input.id || newId(),
    templateType: 'cloze',
    prompt: String(input.prompt || ''),
    blanks: blanks.length ? blanks : [{ answers: [''], caseSensitive: false }],
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

function normaliseOrderingTemplate(input) {
  const rawItems = Array.isArray(input.items) ? input.items : [];
  const items = rawItems.map((it, i) => ({
    id: String(it?.id || orderingItemId()),
    text: String(it?.text || ''),
  }));
  return {
    id: input.id || newId(),
    templateType: 'ordering',
    prompt: String(input.prompt || ''),
    items: items.length ? items : [{ id: orderingItemId(), text: '' }, { id: orderingItemId(), text: '' }],
    shuffleItems: input.shuffleItems !== false,
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

function normaliseMatchingTemplate(input) {
  const rawPairs = Array.isArray(input.pairs) ? input.pairs : [];
  const pairs = rawPairs.map((p) => ({
    id: String(p?.id || matchingPairId()),
    left: String(p?.left || ''),
    right: String(p?.right || ''),
  }));
  return {
    id: input.id || newId(),
    templateType: 'matching',
    prompt: String(input.prompt || ''),
    pairs: pairs.length ? pairs : [
      { id: matchingPairId(), left: '', right: '' },
      { id: matchingPairId(), left: '', right: '' },
    ],
    shuffleRight: input.shuffleRight !== false,
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

function normaliseBucketSortTemplate(input) {
  const rawBuckets = Array.isArray(input.buckets) ? input.buckets : [];
  const buckets = rawBuckets.map((b, i) => ({
    id: String(b?.id || bucketId()),
    label: String(b?.label || ''),
  }));
  const finalBuckets = buckets.length ? buckets : [
    { id: bucketId(), label: '' },
    { id: bucketId(), label: '' },
  ];
  const validBucketIds = new Set(finalBuckets.map((b) => b.id));
  const rawItems = Array.isArray(input.items) ? input.items : [];
  const items = rawItems.map((it) => ({
    id: String(it?.id || bucketItemId()),
    text: String(it?.text || ''),
    bucketId: validBucketIds.has(String(it?.bucketId || '')) ? String(it.bucketId) : finalBuckets[0].id,
  }));
  return {
    id: input.id || newId(),
    templateType: 'bucket-sort',
    prompt: String(input.prompt || ''),
    buckets: finalBuckets,
    items: items.length ? items : [{ id: bucketItemId(), text: '', bucketId: finalBuckets[0].id }],
    shuffleItems: input.shuffleItems !== false,
    disciplines: normaliseDisciplines(input.disciplines),
    notes: String(input.notes || ''),
  };
}

// Cross-cutting fields that any template type may carry. Preserved
// across normalisation so the per-type normalisers can stay narrow:
//   - sourceRef / sourceLabel: provenance for items imported from the
//     library or a sibling set
//   - availableForRandom: when false, the question is excluded from
//     random Mixed-revision runs (still playable inside its curated set)
//   - calculation: not authored in the unified editor but appears in
//     legacy revision sets via sourceRef — passed through as-is
function preserveSharedFields(input, normalised) {
  const out = { ...normalised };
  if (input.sourceRef) out.sourceRef = input.sourceRef;
  if (input.sourceLabel) out.sourceLabel = input.sourceLabel;
  if (input.availableForRandom === false) out.availableForRandom = false;
  return out;
}

function normaliseTemplate(input) {
  if (!input || typeof input !== 'object') return emptyMcqTemplate();
  const type = input.templateType || 'mcq';
  // sourceRef-tagged calculation / fixed templates flow straight through
  // — their payload was validated at the source.
  if (input.sourceRef && (type === 'calculation' || type === 'fixed')) {
    return preserveSharedFields(input, { ...input, templateType: type, id: input.id || `tpl-${Math.random().toString(36).slice(2, 10)}` });
  }
  let normalised;
  if (type === 'true-false') normalised = normaliseTrueFalseTemplate(input);
  else if (type === 'multi-mcq') normalised = normaliseMultiMcqTemplate(input);
  else if (type === 'cloze') normalised = normaliseClozeTemplate(input);
  else if (type === 'ordering') normalised = normaliseOrderingTemplate(input);
  else if (type === 'bucket-sort') normalised = normaliseBucketSortTemplate(input);
  else if (type === 'matching') normalised = normaliseMatchingTemplate(input);
  else if (type === 'fixed') normalised = { ...emptyFixedTemplate(), ...input, templateType: 'fixed' };
  else normalised = normaliseMcqTemplate(input);
  return preserveSharedFields(input, normalised);
}

// Count {{n}} markers in a cloze prompt — used by the editor to warn when
// the prompt's marker count diverges from the blanks array length.
export function countClozeMarkers(prompt) {
  return (String(prompt || '').match(/\{\{\d+\}\}/g) || []).length;
}

export function normaliseMcqExercise(input) {
  if (!input || typeof input !== 'object') return emptyMcqExercise();
  const payload = input.payload && typeof input.payload === 'object' ? input.payload : {};
  const templates = Array.isArray(input.templates) && input.templates.length
    ? input.templates
    : (Array.isArray(payload.templates) ? payload.templates : []);
  const settingsSource = input.settings && typeof input.settings === 'object'
    ? input.settings
    : (payload.settings && typeof payload.settings === 'object' ? payload.settings : {});
  const disciplineMode = typeof input.disciplineMode === 'boolean'
    ? input.disciplineMode
    : Boolean(payload.disciplineMode);
  const rawMode = typeof input.mode === 'string' ? input.mode : payload.mode;
  const mode = rawMode === 'challenge' ? 'challenge' : 'learn';
  const difficultyRaw = String(input.difficulty || '').trim().toLowerCase();
  return {
    id: input.id || '',
    title: String(input.title || '').trim(),
    summary: String(input.summary || ''),
    status: input.status || 'draft',
    freeTierEligible: Boolean(input.freeTierEligible),
    category: String(input.category || '').trim(),
    difficulty: MCQ_DIFFICULTIES.includes(difficultyRaw) ? difficultyRaw : '',
    isCuratedRevisionSet: Boolean(input.isCuratedRevisionSet),
    disciplineMode,
    mode,
    settings: {
      questionCount: Number.isFinite(Number(settingsSource.questionCount))
        ? Math.max(1, Math.floor(Number(settingsSource.questionCount)))
        : 5,
      shuffle: settingsSource.shuffle !== false,
    },
    templates: templates.length ? templates.map(normaliseTemplate) : [emptyMcqTemplate()],
  };
}

export function validateMcqExercise(exercise) {
  const issues = [];
  const norm = normaliseMcqExercise(exercise);
  if (!norm.title) issues.push({ key: 'title', message: 'Exercise title is required.' });
  if (!norm.templates.length) issues.push({ key: 'templates', message: 'Add at least one question.' });
  norm.templates.forEach((t, index) => {
    if (!t.prompt.trim()) {
      issues.push({ key: `template-${index}-prompt`, message: `Question ${index + 1}: prompt is required.` });
    }
    if (t.templateType === 'true-false') {
      if (t.correctAnswer !== true && t.correctAnswer !== false) {
        issues.push({ key: `template-${index}-tf`, message: `Question ${index + 1}: pick True or False as the answer.` });
      }
      return;
    }
    if (t.templateType === 'cloze') {
      const blanks = Array.isArray(t.blanks) ? t.blanks : [];
      if (!blanks.length) {
        issues.push({ key: `template-${index}-blanks`, message: `Question ${index + 1}: add at least one blank.` });
        return;
      }
      const markerCount = countClozeMarkers(t.prompt);
      if (markerCount !== blanks.length) {
        issues.push({ key: `template-${index}-cloze-markers`, message: `Question ${index + 1}: the prompt has ${markerCount} {{n}} marker(s) but ${blanks.length} blank(s) — make them match.` });
      }
      blanks.forEach((b, bi) => {
        const answers = Array.isArray(b?.answers) ? b.answers.map((a) => String(a || '').trim()).filter(Boolean) : [];
        if (!answers.length) {
          issues.push({ key: `template-${index}-cloze-${bi}`, message: `Question ${index + 1}: blank ${bi + 1} needs at least one accepted answer.` });
        }
      });
      return;
    }
    if (t.templateType === 'ordering') {
      const items = Array.isArray(t.items) ? t.items.filter((it) => String(it?.text || '').trim()) : [];
      if (items.length < 2) {
        issues.push({ key: `template-${index}-ordering`, message: `Question ${index + 1}: add at least 2 items to put in order.` });
      }
      return;
    }
    if (t.templateType === 'bucket-sort') {
      const buckets = Array.isArray(t.buckets) ? t.buckets.filter((b) => String(b?.label || '').trim()) : [];
      const items = Array.isArray(t.items) ? t.items.filter((it) => String(it?.text || '').trim()) : [];
      const bucketIdSet = new Set(buckets.map((b) => String(b.id)));
      if (buckets.length < 2) {
        issues.push({ key: `template-${index}-bs-buckets`, message: `Question ${index + 1}: add at least 2 labelled buckets.` });
      }
      if (items.length < 2) {
        issues.push({ key: `template-${index}-bs-items`, message: `Question ${index + 1}: add at least 2 items.` });
      }
      const unassigned = items.filter((it) => !bucketIdSet.has(String(it.bucketId)));
      if (unassigned.length) {
        issues.push({ key: `template-${index}-bs-orphan`, message: `Question ${index + 1}: every item needs a bucket — ${unassigned.length} unassigned.` });
      }
      return;
    }
    if (t.templateType === 'matching') {
      const pairs = Array.isArray(t.pairs) ? t.pairs : [];
      const valid = pairs.filter((p) => String(p?.left || '').trim() && String(p?.right || '').trim());
      if (valid.length < 2) {
        issues.push({ key: `template-${index}-match`, message: `Question ${index + 1}: add at least 2 pairs (both sides filled in).` });
      }
      return;
    }
    const opts = (t.options || []).map((o) => String(o || '').trim()).filter(Boolean);
    if (opts.length < 2) issues.push({ key: `template-${index}-options`, message: `Question ${index + 1}: add at least 2 options.` });
    if (opts.length > 8) issues.push({ key: `template-${index}-options-cap`, message: `Question ${index + 1}: keep options to 8 or fewer.` });
    if (t.templateType === 'multi-mcq') {
      const validIndices = (t.correctIndices || []).filter((i) => i >= 0 && i < opts.length);
      if (!validIndices.length) {
        issues.push({ key: `template-${index}-multi-empty`, message: `Question ${index + 1}: mark at least one correct option.` });
      } else if (validIndices.length >= opts.length) {
        issues.push({ key: `template-${index}-multi-all`, message: `Question ${index + 1}: at least one option must be incorrect.` });
      }
      return;
    }
    if (!Number.isFinite(Number(t.correctIndex)) || Number(t.correctIndex) < 0 || Number(t.correctIndex) >= opts.length) {
      issues.push({ key: `template-${index}-correct`, message: `Question ${index + 1}: pick which option is the correct answer.` });
    }
  });
  return issues;
}

export function getMcqDisciplineCoverage(exercise) {
  const tpls = Array.isArray(exercise?.templates) ? exercise.templates : [];
  const seen = new Set();
  for (const t of tpls) {
    const tags = Array.isArray(t.disciplines) ? t.disciplines : [];
    for (const tag of tags) {
      if (QUESTION_DISCIPLINES.includes(tag)) seen.add(tag);
    }
  }
  return Array.from(seen);
}

export function getQbankTypeCounts(exercise) {
  const tpls = Array.isArray(exercise?.templates) ? exercise.templates : [];
  const counts = { 'mcq': 0, 'true-false': 0, 'multi-mcq': 0, 'cloze': 0, 'ordering': 0, 'bucket-sort': 0, 'matching': 0 };
  for (const t of tpls) {
    const type = t?.templateType || 'mcq';
    if (counts[type] !== undefined) counts[type] += 1;
  }
  return counts;
}
